import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  PencilIcon,
  XMarkIcon,
  DocumentTextIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const CreditNoteDetails = ({ onTabChange }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const creditNoteId = id;
  const [creditNote, setCreditNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [downloadDropdown, setDownloadDropdown] = useState(false);
  const [printDropdown, setPrintDropdown] = useState(false);

  // Fetch credit note details
  const fetchCreditNote = async () => {
    try {
      setLoading(true);
      
      // Check if JWT token exists
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError(t('creditNoteDetails.noTokenError'));
        setLoading(false);
        return;
      }
      
      const response = await fetch(`/api/finance/credit-notes/${creditNoteId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        // JWT expired or invalid
        setError(t('creditNoteDetails.authenticationError'));
        // Clear the expired token
        localStorage.removeItem('jwtToken');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('creditNoteDetails.failedToFetchCreditNote'));
      }

      const data = await response.json();
      setCreditNote(data);
    } catch (error) {
      console.error('Error fetching credit note:', error);
      if (error.message.includes('jwt') || error.message.includes('token')) {
        setError(t('creditNoteDetails.authenticationError'));
      } else {
        setError(error.message || t('creditNoteDetails.failedToLoadCreditNoteDetails'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (creditNoteId) {
      // Only fetch if we have a JWT token
      const token = localStorage.getItem('jwtToken');
      if (token) {
        fetchCreditNote();
      } else {
        setError(t('creditNoteDetails.noTokenError'));
        setLoading(false);
      }
    }
  }, [creditNoteId]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle download
  const handleDownload = async (creditNoteType = 'ORIGINAL') => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError(t('creditNoteDetails.noTokenError'));
        return;
      }
      
      const response = await fetch(`/api/finance/credit-notes/${creditNoteId}/pdf?type=${creditNoteType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        setError(t('creditNoteDetails.authenticationError'));
        localStorage.removeItem('jwtToken');
        return;
      }

      if (!response.ok) {
        throw new Error(t('creditNoteDetails.failedToDownloadCreditNote'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit-note-${creditNoteType.toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccessMessage(t('creditNoteDetails.downloadSuccess', { type: creditNoteType }));
      setDownloadDropdown(false);
    } catch (error) {
      console.error('Error downloading credit note:', error);
      setError(error.message || t('creditNoteDetails.failedToDownloadCreditNote'));
    }
  };

  // Handle print
  const handlePrint = async (creditNoteType = 'ORIGINAL') => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError(t('creditNoteDetails.noTokenError'));
        return;
      }
      
      const response = await fetch(`/api/finance/credit-notes/${creditNoteId}/pdf?type=${creditNoteType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        setError(t('creditNoteDetails.authenticationError'));
        localStorage.removeItem('jwtToken');
        return;
      }

      if (!response.ok) {
        throw new Error(t('creditNoteDetails.failedToPrintCreditNote'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        window.URL.revokeObjectURL(url);
      }, 1000);

      setSuccessMessage(t('creditNoteDetails.printSuccess', { type: creditNoteType }));
      setPrintDropdown(false);
    } catch (error) {
      console.error('Error printing credit note:', error);
      setError(error.message || t('creditNoteDetails.failedToPrintCreditNote'));
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'Draft': { bg: 'bg-gray-300', text: 'text-gray-800' },
      'Sent': { bg: 'bg-blue-100', text: 'text-blue-800' },
      'Paid': { bg: 'bg-green-100', text: 'text-green-800' },
      'Partial': { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      'Overdue': { bg: 'bg-red-100', text: 'text-red-800' },
      'Cancelled': { bg: 'bg-gray-100', text: 'text-gray-600' }
    };

    const config = statusConfig[status] || statusConfig['Draft'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {t(`creditNoteDetails.status${status}`)}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'BGN'
    }).format(amount);
  };

  // Format EUR currency
  const formatEuro = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format description with line breaks
  const formatDescription = (description) => {
    if (!description) return '';
    return description.split('\n').map((line, index) => (
      <span key={index}>
        {line}
        {index < description.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if we have a valid JWT token
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-8 rounded-lg text-center">
            <XMarkIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('creditNoteDetails.authenticationRequired')}</h2>
            <p className="text-red-700 mb-6">{t('creditNoteDetails.pleaseLoginToContinue')}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {t('creditNoteDetails.refreshAndLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !creditNote) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <XMarkIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <div className="text-red-600 mb-4 text-lg font-medium">
              {error || t('creditNoteDetails.creditNoteNotFound')}
            </div>
            {error === t('creditNoteDetails.authenticationError') && (
              <div className="mb-6">
                <p className="text-red-700 mb-4">{t('creditNoteDetails.pleaseLoginAgain')}</p>
                <button
                  onClick={() => {
                    localStorage.removeItem('jwtToken');
                    window.location.reload();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  {t('creditNoteDetails.refreshAndLogin')}
                </button>
              </div>
            )}
            <button
              onClick={() => navigate('/admin/dashboard/invoices?tab=credit-notes')}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {t('creditNoteDetails.backToCreditNotes')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage('')}
            className="text-green-600 hover:text-green-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard/invoices?tab=credit-notes')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{creditNote.creditNoteNumber}</h1>
                <p className="text-gray-600">{t('creditNoteDetails.creditNoteDetails')}</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <div className="relative">
                <button 
                  onClick={() => setDownloadDropdown(!downloadDropdown)}
                  className="text-green-600 hover:text-green-900 p-2"
                  title="Download as PDF"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                {downloadDropdown && (
                  <div 
                    className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleDownload('ORIGINAL');
                          setDownloadDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Download Original
                      </button>
                      <button
                        onClick={() => {
                          handleDownload('COPY');
                          setDownloadDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Download Copy
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                <button 
                  onClick={() => setPrintDropdown(!printDropdown)}
                  className="text-purple-600 hover:text-purple-900 p-2"
                  title="Print PDF"
                >
                  <PrinterIcon className="h-5 w-5" />
                </button>
                {printDropdown && (
                  <div 
                    className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handlePrint('ORIGINAL');
                          setPrintDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Print Original
                      </button>
                      <button
                        onClick={() => {
                          handlePrint('COPY');
                          setPrintDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Print Copy
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button 
                onClick={() => onTabChange('credit-note-edit', { id: creditNote._id })}
                className="text-blue-600 hover:text-blue-900 p-2"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button 
                onClick={() => onTabChange('credit-notes')}
                className="text-red-600 hover:text-red-900 p-2"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
          {/* Credit Note Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('creditNoteDetails.creditNoteInformation')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-sm text-gray-600">{t('creditNoteDetails.creditNoteNumber')}:</span>
                <p className="font-medium">{creditNote.creditNoteNumber}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('creditNoteDetails.originalInvoice')}:</span>
                <p className="font-medium">{creditNote.originalInvoiceNumber}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('creditNoteDetails.issueDate')}:</span>
                <p className="font-medium">{formatDate(creditNote.issueDate)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('creditNoteDetails.dueDate')}:</span>
                <p className="font-medium">{formatDate(creditNote.dueDate)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('creditNoteDetails.status')}:</span>
                <div className="mt-1">{getStatusBadge(creditNote.status)}</div>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('creditNoteDetails.currency')}:</span>
                <p className="font-medium">{creditNote.currency}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('creditNoteDetails.type')}:</span>
                <p className="font-medium">{t(`creditNoteDetails.type${creditNote.invoiceType || 'ORIGINAL'}`)}</p>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('creditNoteDetails.items')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('creditNoteDetails.no')}</th>
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('creditNoteDetails.description')}</th>
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('creditNoteDetails.quantity')}</th>
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('creditNoteDetails.unitPrice')}</th>
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('creditNoteDetails.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {creditNote.items.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 px-2 text-sm">{index + 1}.</td>
                      <td className="py-2 px-2 text-sm">
                        <div className="whitespace-pre-line">{formatDescription(item.description)}</div>
                      </td>
                      <td className="py-2 px-2 text-sm">{item.quantity}</td>
                      <td className="py-2 px-2 text-sm">{formatCurrency(item.unitPrice)}</td>
                      <td className="py-2 px-2 text-sm font-medium">{formatCurrency(item.quantity * item.unitPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('creditNoteDetails.summary')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* BGN Totals */}
              <div className="space-y-3">
                <h3 className="text-md font-medium text-gray-900">{t('creditNoteDetails.bgn')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('creditNoteDetails.subtotal')}:</span>
                    <span className="font-medium">{formatCurrency(creditNote.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('creditNoteDetails.taxAmount')}:</span>
                    <span className="font-medium">{formatCurrency(creditNote.taxAmount)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-medium">{t('creditNoteDetails.totalAmount')}:</span>
                    <span className="text-green-600 font-bold">{formatCurrency(creditNote.totalAmount)}</span>
                  </div>
                </div>
              </div>

              {/* EUR Totals */}
              <div className="space-y-3">
                <h3 className="text-md font-medium text-gray-900">{t('creditNoteDetails.eur')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('creditNoteDetails.subtotal')}:</span>
                    <span className="font-medium">{formatEuro(creditNote.subtotal / 1.95583)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('creditNoteDetails.taxAmount')}:</span>
                    <span className="font-medium">{formatEuro(creditNote.taxAmount / 1.95583)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-medium">{t('creditNoteDetails.totalAmount')}:</span>
                    <span className="text-green-600 font-bold">{formatEuro(creditNote.totalAmount / 1.95583)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {creditNote.notes && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('creditNoteDetails.notes')}</h2>
              <p className="text-gray-700 whitespace-pre-line">{creditNote.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Company Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('creditNoteDetails.companyInformation')}</h2>
            <div className="space-y-2">
              <p className="font-medium">{creditNote.companyId?.name}</p>
              <p className="text-sm text-gray-600">{creditNote.companyId?.address}</p>
              <p className="text-sm text-gray-600">{t('creditNoteDetails.mol')}: {creditNote.companyId?.materiallyResponsiblePerson?.name}</p>
              {creditNote.companyId?.vatTaxId && creditNote.companyId.vatTaxId.trim() !== '' ? (
                <p className="text-sm text-gray-600">{t('creditNoteDetails.vatTaxId')}: BG{creditNote.companyId.vatTaxId}</p>
              ) : (
                <p className="text-sm text-gray-600">{t('creditNoteDetails.taxId')}: {creditNote.companyId?.taxId}</p>
              )}
            </div>
          </div>

          {/* Partner Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('creditNoteDetails.partnerInformation')}</h2>
            <div className="space-y-2">
              <p className="font-medium">{creditNote.partnerId?.name}</p>
              <p className="text-sm text-gray-600">{creditNote.partnerId?.address}</p>
              <p className="text-sm text-gray-600">{t('creditNoteDetails.contact')}: {creditNote.partnerId?.contactPerson}</p>
              {creditNote.partnerId?.vatTaxId && creditNote.partnerId.vatTaxId.trim() !== '' ? (
                <p className="text-sm text-gray-600">{t('creditNoteDetails.vatTaxId')}: BG{creditNote.partnerId.vatTaxId}</p>
              ) : (
                <p className="text-sm text-gray-600">{t('creditNoteDetails.taxId')}: {creditNote.partnerId?.taxId}</p>
              )}
            </div>
          </div>

          {/* Bank Account Information */}
          {creditNote.selectedBankAccount && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('creditNoteDetails.bankAccountInformation')}</h2>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">{t('creditNoteDetails.bank')}: {creditNote.selectedBankAccount.bankName}</p>
                <p className="text-sm text-gray-600">{t('creditNoteDetails.bic')}: {creditNote.selectedBankAccount.swiftCode}</p>
                <p className="text-sm text-gray-600">{t('creditNoteDetails.iban')}: {creditNote.selectedBankAccount.iban}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default CreditNoteDetails; 